import * as signalR from '@microsoft/signalr';
import { API_URL } from '@/http';
import { auth } from '@/auth';
import { ISearchDocResults } from '@models/search/ISearchDocResultItem';

type CompleteCallback = (data: ISearchDocResults | undefined, success: boolean) => void;

class AISearchHub {
    private connection: signalR.HubConnection;
    private requestId: string = '';
    private onCompleteCallback: CompleteCallback | null = null;
    /**
     * Хаб может доложить о готовности раньше, чем POST /searchDocsAI вернёт id запроса.
     * Раньше такое событие терялось (id !== '') и поиск висел до отмены — придерживаем
     * его до `setRequestId`.
     */
    private pending = new Map<string, { data: ISearchDocResults | undefined; success: boolean }>();

    constructor() {
        const logLevel = import.meta.env.PROD ? signalR.LogLevel.Warning : signalR.LogLevel.Trace;

        this.connection = new signalR.HubConnectionBuilder()
            .configureLogging(logLevel)
            .withUrl(`${API_URL}/hubs/aiSearch`, {
                accessTokenFactory: () => auth.pageAccessToken() ?? '',
            })
            .build();

        this.connection.on('aiSearchCompleted', (id: string, data: ISearchDocResults, success: boolean) => {
            if (!this.requestId) {
                this.pending.set(id, { data, success });
                return;
            }
            if (id === this.requestId) {
                this.deliver(data, success);
            }
        });
    }

    public async start(): Promise<void> {
        if (this.connection.state === signalR.HubConnectionState.Disconnected) {
            await this.connection.start();
        }
    }

    public stop(): void {
        this.reset();
        if (this.connection.state === signalR.HubConnectionState.Connected) {
            void this.connection.stop();
        }
    }

    public setRequestId(id: string): void {
        this.requestId = id;
        const early = this.pending.get(id);
        if (early) {
            this.deliver(early.data, early.success);
        }
    }

    public onComplete(callback: CompleteCallback): void {
        this.reset();
        this.onCompleteCallback = callback;
    }

    private deliver(data: ISearchDocResults | undefined, success: boolean) {
        const callback = this.onCompleteCallback;
        this.reset();
        callback?.(data, success);
    }

    private reset() {
        this.onCompleteCallback = null;
        this.requestId = '';
        this.pending.clear();
    }
}

export const aiSearchHub = new AISearchHub();
